import * as React from 'react'
import {fromJust} from '@freckle/maybe'
import {useSafeEffectExtraDeps, unsafeMkCallbackFn} from '@freckle/react-hooks'

import {type DistrictT} from '@frontrow/console/src/models/district'
import {type GroupedArtifactData} from '@frontrow/console/src/entities/models/artifact'
import CsvStudentLevelUpload from '@frontrow/console/src/components/pages/students-page/csv-student-level-upload/csv-student-level-upload'
import CsvStudentLevelPreview from '@frontrow/console/src/components/pages/students-page/csv-student-level-preview'
import {
  useStudentCsvUpload,
  type CsvUploadLevelsT,
  type CsvUploadAssessmentT
} from '@frontrow/console/src/hooks/use-student-csv-upload'

type State = {
  uploadType: CsvUploadLevelsT
  uploadAssessmentType: CsvUploadAssessmentT
  selectedDistrict: DistrictT | undefined | null
  file: File | undefined | null
  overrideRecentPractice: boolean
}

export default function CsvStudentLevelUploadContainer(): React.ReactElement {
  const [state, setState] = React.useState<State>({
    uploadType: 'Math',
    uploadAssessmentType: {tag: 'MAP'},
    selectedDistrict: undefined,
    file: null,
    overrideRecentPractice: false
  })

  const {
    parsedStudentLevelCsvData,
    csvPreviewParsingError,
    updateStatus,
    warnings,
    csvUpdateError,
    operationLocation,
    artifactData,
    previewCsvFile,
    clearCsvPreview,
    updateStudentLevels: updateStudentLevelsAction,
    updateStudentLevelsCsvResponse,
    updateStudentLevelsCsvError
  } = useStudentCsvUpload()

  const updateStudentLevelsSuccess = unsafeMkCallbackFn((artifactData: GroupedArtifactData) => {
    updateStudentLevelsCsvResponse(artifactData)
  })

  const updateStudentLevelsError = unsafeMkCallbackFn(() => {
    updateStudentLevelsCsvError("We failed to update some student's levels.")
  })

  // useEffect hook for callbacks on state property changes
  useSafeEffectExtraDeps<{
    selectedDistrict: typeof state.selectedDistrict
    uploadAssessmentType: typeof state.uploadAssessmentType
    file: typeof state.file
    previewCsvFile: typeof previewCsvFile
  }>(
    ({selectedDistrict, uploadAssessmentType, file, previewCsvFile}) => {
      if (!selectedDistrict) {
        return
      }
      const district = fromJust(selectedDistrict, 'Unable to preview CSV file without district')

      if (file) {
        previewCsvFile(district.id, file, state.uploadType, uploadAssessmentType)
      }
    },
    [state.uploadType],
    {
      selectedDistrict: {
        value: state.selectedDistrict,
        comparator: (district1?: DistrictT | null, district2?: DistrictT | null) =>
          district1?.id === district2?.id
      },
      uploadAssessmentType: {
        value: state.uploadAssessmentType,
        comparator: ({tag: tag1}, {tag: tag2}) => tag1 === tag2
      },
      file: {
        value: state.file,
        comparator: (file1?: File | null, file2?: File | null) =>
          file1?.name === file2?.name &&
          file1?.lastModified === file2?.lastModified &&
          file1?.size === file2?.size
      },
      previewCsvFile: {
        value: previewCsvFile,
        comparator: () => true
      }
    }
  )

  useSafeEffectExtraDeps(
    ({clearCsvPreview}: {clearCsvPreview: () => void}) => {
      clearCsvPreview()
    },
    [],
    {
      clearCsvPreview: {
        value: clearCsvPreview,
        comparator: () => true
      }
    }
  )

  const previewStudentLevels = (file: File): void => {
    setState({...state, file})
  }

  function updateStudentLevelsWithCurrentData() {
    const {
      file: mFile,
      selectedDistrict: mDistrict,
      uploadType,
      uploadAssessmentType,
      overrideRecentPractice
    } = state

    const district = fromJust(mDistrict, 'Unable to upload CSV file without district')
    const file = fromJust(mFile, 'Unable to upload CSV file without district')

    updateStudentLevelsAction(
      district.id,
      file,
      uploadType,
      uploadAssessmentType,
      overrideRecentPractice
    )
  }

  function onPostDistrictFetch(districts: Array<DistrictT>) {
    if (districts.length === 0) {
      setState({...state, selectedDistrict: null, file: null})
    } else if (districts.length === 1) {
      setState({...state, selectedDistrict: districts[0], file: null})
    } else {
      const firstDistrict = districts[0]
      throw new Error(`found multiple districts with id ${firstDistrict.id}`)
    }
  }

  function selectUploadType(newType: CsvUploadLevelsT) {
    clearCsvPreview()

    setState(prevState => ({
      ...prevState,
      uploadType: newType
    }))
  }

  function selectUploadAssessmentType(newType: CsvUploadAssessmentT) {
    clearCsvPreview()
    setState(prevState => ({
      ...prevState,
      uploadAssessmentType: newType
    }))
  }

  function setOverrideRecentPractice(newOverrideRecentPractice: boolean) {
    setState(prevState => ({
      ...prevState,
      overrideRecentPractice: newOverrideRecentPractice
    }))
  }

  const preview =
    updateStatus !== null && updateStatus !== undefined ? (
      <CsvStudentLevelPreview
        studentLevelData={parsedStudentLevelCsvData}
        warnings={warnings}
        csvPreviewParsingError={csvPreviewParsingError}
        csvUpdateError={csvUpdateError}
        // eslint-disable-next-line react/jsx-no-bind
        onUpdate={updateStudentLevelsWithCurrentData}
        updateStatus={updateStatus}
        operationLocation={operationLocation}
        updateStudentLevelsSuccess={updateStudentLevelsSuccess}
        updateStudentLevelsError={updateStudentLevelsError}
        artifactData={artifactData}
      />
    ) : null

  return (
    <>
      <CsvStudentLevelUpload
        district={state.selectedDistrict}
        // eslint-disable-next-line react/jsx-no-bind
        onPostFetch={onPostDistrictFetch}
        onFileUploaded={previewStudentLevels}
        // eslint-disable-next-line react/jsx-no-bind
        selectUploadType={selectUploadType}
        // eslint-disable-next-line react/jsx-no-bind
        selectUploadAssessmentType={selectUploadAssessmentType}
        // eslint-disable-next-line react/jsx-no-bind
        setOverrideRecentPractice={setOverrideRecentPractice}
        uploadType={state.uploadType}
        overrideRecentPractice={state.overrideRecentPractice}
        uploadAssessmentType={state.uploadAssessmentType}
      />
      {preview}
    </>
  )
}
